import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Search, Trash2, FileText, LogIn, Mail, AlertCircle, CheckCircle, Clock, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const actionConfig = {
    login: { icon: LogIn, color: 'text-blue-600 bg-blue-50', label: 'Login' },
    quote_created: { icon: FileText, color: 'text-green-600 bg-green-50', label: 'Quote Created' },
    status_change: { icon: RefreshCw, color: 'text-amber-600 bg-amber-50', label: 'Status Change' },
    email_sent: { icon: Mail, color: 'text-indigo-600 bg-indigo-50', label: 'Email Sent' },
    email_error: { icon: AlertCircle, color: 'text-red-600 bg-red-50', label: 'Email Error' },
    user_deleted: { icon: Trash2, color: 'text-red-600 bg-red-50', label: 'User Deleted' },
    customer_deleted: { icon: Trash2, color: 'text-red-600 bg-red-50', label: 'Customer Deleted' },
    quote_expired: { icon: Clock, color: 'text-gray-600 bg-gray-50', label: 'Quote Expired' },
    log_cleanup: { icon: Trash2, color: 'text-gray-600 bg-gray-50', label: 'Log Cleanup' }
};

const LogsPage = () => {
    const { token } = useAuth();
    const [logsData, setLogsData] = useState({ logs: [], total: 0, page: 1, total_pages: 1, summary: {} });
    const [loading, setLoading] = useState(true);
    const [cleaning, setCleaning] = useState(false);
    const [actionFilter, setActionFilter] = useState('all');
    const [page, setPage] = useState(1);

    useEffect(() => { fetchLogs(); }, [actionFilter, page]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: page.toString(), per_page: '50' });
            if (actionFilter !== 'all') params.append('action', actionFilter);
            const response = await axios.get(`${API}/admin/logs?${params}`, { headers: { Authorization: `Bearer ${token}` } });
            setLogsData(response.data);
        } catch { toast.error('Failed to load logs'); }
        finally { setLoading(false); }
    };

    const handleCleanup = async () => {
        if (!window.confirm('Are you sure you want to clean up old logs?')) return;
        setCleaning(true);
        try {
            const response = await axios.post(`${API}/admin/logs/cleanup`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(response.data.message);
            fetchLogs();
        } catch { toast.error('Failed to cleanup logs'); }
        finally { setCleaning(false); }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
            date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    const getActionDisplay = (action) => {
        const config = actionConfig[action] || { icon: FileText, color: 'text-gray-600 bg-gray-50', label: action };
        return config;
    };

    const totalLogs = Object.values(logsData.summary).reduce((a, b) => a + b, 0);

    return (
        <div className="max-w-5xl mx-auto animate-fade-in space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-heading text-brand-charcoal">System Logs</h1>
                    <p className="text-brand-gray-500 mt-1">Activity logs and audit trail</p>
                </div>
                <Button variant="outline" onClick={handleCleanup} disabled={cleaning} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="cleanup-logs-btn">
                    {cleaning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Cleanup Old Logs
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                <div className="p-3 bg-white border border-brand-gray-200 cursor-pointer hover:border-brand-lime/50 transition-colors" onClick={() => { setActionFilter('all'); setPage(1); }} data-testid="log-summary-total">
                    <p className="text-xs text-brand-gray-500 uppercase tracking-wide">Total Logs</p>
                    <p className="text-2xl font-bold font-heading text-brand-charcoal">{totalLogs}</p>
                </div>
                {Object.entries(logsData.summary).map(([action, count]) => {
                    const display = getActionDisplay(action);
                    return (
                        <div key={action} className={`p-3 bg-white border cursor-pointer transition-colors ${actionFilter === action ? 'border-brand-lime ring-2 ring-brand-lime/30' : 'border-brand-gray-200 hover:border-brand-lime/50'}`}
                            onClick={() => { setActionFilter(actionFilter === action ? 'all' : action); setPage(1); }}
                            data-testid={`log-summary-${action}`}>
                            <p className="text-xs text-brand-gray-500 uppercase tracking-wide truncate">{display.label}</p>
                            <p className="text-2xl font-bold font-heading text-brand-charcoal">{count}</p>
                        </div>
                    );
                })}
            </div>

            {/* Logs List */}
            <Card className="border-0 shadow-lg">
                <CardHeader className="pb-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Select value={actionFilter} onValueChange={(val) => { setActionFilter(val); setPage(1); }}>
                            <SelectTrigger className="h-11 w-[200px]" data-testid="log-action-filter">
                                <SelectValue placeholder="Filter by action" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Actions</SelectItem>
                                <SelectItem value="login">Logins</SelectItem>
                                <SelectItem value="quote_created">Quote Created</SelectItem>
                                <SelectItem value="status_change">Status Changes</SelectItem>
                                <SelectItem value="email_sent">Emails Sent</SelectItem>
                                <SelectItem value="email_error">Email Errors</SelectItem>
                                <SelectItem value="user_deleted">User Deleted</SelectItem>
                                <SelectItem value="customer_deleted">Customer Deleted</SelectItem>
                                <SelectItem value="quote_expired">Quote Expired</SelectItem>
                                <SelectItem value="log_cleanup">Log Cleanup</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="flex-1 text-right text-sm text-brand-gray-500 self-center">
                            Showing {logsData.logs.length} of {logsData.total} logs
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-brand-lime" /></div>
                    ) : logsData.logs.length === 0 ? (
                        <div className="text-center py-12">
                            <FileText className="h-12 w-12 mx-auto text-brand-gray-300 mb-3" />
                            <p className="text-brand-gray-500">No logs found</p>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {logsData.logs.map((log) => {
                                const display = getActionDisplay(log.action);
                                const ActionIcon = display.icon;
                                return (
                                    <div key={log.id} className="flex items-start gap-3 p-3 hover:bg-brand-gray-50 transition-colors border-b border-brand-gray-100 last:border-0" data-testid={`log-entry-${log.id}`}>
                                        <div className={`h-8 w-8 rounded flex items-center justify-center flex-shrink-0 ${display.color}`}>
                                            <ActionIcon className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium text-sm text-brand-charcoal">{display.label}</span>
                                                {log.level === 'error' && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">ERROR</span>}
                                            </div>
                                            <p className="text-sm text-brand-gray-600 mt-0.5">{log.details}</p>
                                            {log.user_name && (
                                                <p className="text-xs text-brand-gray-400 mt-1">
                                                    By {log.user_name} ({log.user_email})
                                                </p>
                                            )}
                                        </div>
                                        <span className="text-xs text-brand-gray-400 flex-shrink-0 whitespace-nowrap">{formatDate(log.created_at)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    {logsData.total_pages > 1 && (
                        <div className="flex items-center justify-between pt-4 mt-4 border-t border-brand-gray-200">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="logs-prev-page">
                                <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                            </Button>
                            <span className="text-sm text-brand-gray-500">Page {logsData.page} of {logsData.total_pages}</span>
                            <Button variant="outline" size="sm" disabled={page >= logsData.total_pages} onClick={() => setPage(p => p + 1)} data-testid="logs-next-page">
                                Next <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default LogsPage;
